import { SimpleGrid } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as EmptyViewExam } from '../../../assets/images/icons/mevo/exam/empty-view-favorite.svg';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Checkbox } from '../../../components/UI-CHAKRA/Checkbox';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { CustomMenuItem, CustomSelect } from '../../../components/UI-CHAKRA/Select/CustomSelect';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { capitalize } from '../../../helpers/string';
import { Exam, FavoriteExam } from '../../../interfaces/exam';

const ORDER_BY_TYPES = {
  ASCENDING: 'ASCENDING',
  DESCENDING: 'DESCENDING',
};

const ORDER_BY_TYPES_MAP = {
  ASCENDING: 'A-Z',
  DESCENDING: 'Z-A',
};

type OrderByType = (typeof ORDER_BY_TYPES)[keyof typeof ORDER_BY_TYPES];

interface FavoriteExamsProps {
  includedExams: Exam[];
  favoriteExams: FavoriteExam[];
  onRemoveExamFromPrescription(exam: Exam): void;
  onAddFavoriteExamToPrescription(favoriteExam: FavoriteExam): void;
}

const EmptyView = () => {
  return (
    <Flex alignItems={'center'} justifyContent={'center'} gap={'10px'} p={'20px'} h={'100%'}>
      <EmptyViewExam />
      <Flex flexDir={'column'}>
        <Text fontSize={'15px'} color={'primary.main'} lineHeight={'18px'} fontWeight={600}>
          Não há exames favoritos{' '}
        </Text>
        <Text fontSize={'12px'} fontWeight={400} lineHeight={'14px'} color={'typography.dark'}>
          Use a busca acima para adicionar exames
        </Text>
      </Flex>
    </Flex>
  );
};

export default function FavoriteExams(props: FavoriteExamsProps) {
  const [orderBy, setOrderBy] = useState<OrderByType>(ORDER_BY_TYPES.ASCENDING);
  const [selectedExams, setSelectedExams] = useState<string[]>([]);

  const { includedExams, favoriteExams, onAddFavoriteExamToPrescription, onRemoveExamFromPrescription } = props;

  const orderedExams = useMemo(() => {
    const uniqueExams = Array.from(new Map(favoriteExams.map((exam) => [exam.referencia, exam])).values());

    return uniqueExams.sort((a, b) => {
      if (orderBy === ORDER_BY_TYPES.ASCENDING) {
        return a.name.localeCompare(b.name);
      }
      return b.name.localeCompare(a.name);
    });
  }, [orderBy, favoriteExams]);

  const onSelectExam = useCallback(
    async (favoriteExam: FavoriteExam) => {
      setSelectedExams((prev: string[]) => {
        const isAlreadSelected = prev.find((exam) => exam === favoriteExam.referencia);

        if (isAlreadSelected) {
          const includedExam = includedExams.find((exam) => exam.referencia === favoriteExam.referencia);
          if (!includedExam) return prev;
          onRemoveExamFromPrescription(includedExam);
          return prev.filter((exam) => exam !== favoriteExam.referencia);
        }

        onAddFavoriteExamToPrescription(favoriteExam);
        return [...prev, favoriteExam.referencia];
      });
    },
    [includedExams, onAddFavoriteExamToPrescription, onRemoveExamFromPrescription]
  );

  useEffect(() => {
    const includedExamReferences = includedExams.map((exam) => exam.referencia);
    const favoriteExamReferences = favoriteExams.map((exam) => exam.referencia);
    const filteredExams = includedExamReferences.filter((ref) => favoriteExamReferences.includes(ref as string));
    setSelectedExams(filteredExams as string[]);
  }, [favoriteExams, includedExams]);

  const Content = useMemo(() => {
    if (favoriteExams.length === 0) {
      return <EmptyView />;
    }
    return (
      <Box boxShadow={'0px 4px 104px 0px rgba(0, 0, 0, 0.05)'} w={'100%'}>
        <Box
          bg={'#fff'}
          padding={'18px 20px 15px 20px'}
          display={'flex'}
          flexDir={'column'}
          gap={'8px'}
          borderRadius={'10px 10px 0 0'}
        >
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Text fontSize={'15px'} fontWeight={600} color={'primary.main'}>
              Exames Favoritos
            </Text>
            <Flex justifyContent={'center'} alignItems={'center'} gap={'10px'}>
              <Text fontSize={'14px'} fontWeight={400}>
                Ordenar por
              </Text>
              <CustomSelect value={ORDER_BY_TYPES_MAP[orderBy as keyof typeof ORDER_BY_TYPES_MAP]} placeholder={''}>
                <CustomMenuItem onClick={() => setOrderBy(ORDER_BY_TYPES.ASCENDING)} value={'A-Z'} label={'A-Z'} />
                <CustomMenuItem onClick={() => setOrderBy(ORDER_BY_TYPES.DESCENDING)} value={'Z-A'} label={'Z-A'} />
              </CustomSelect>
            </Flex>
          </Flex>

          <SimpleGrid
            columns={{ base: 1, sm: 2 }}
            spacingX={'32px'}
            spacingY={'16px'}
            mt={'32px'}
            overflowY={'auto'}
            maxHeight={{ base: '250px', md: '500px' }}
            pr={'10px'}
          >
            {orderedExams.map((exam) => (
              <Checkbox
                key={exam.referencia}
                isChecked={selectedExams.includes(exam.referencia)}
                onChange={() => onSelectExam(exam)}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                <Text color={'#343A40'} fontSize={'12px'} fontWeight={500} ml={'10px'}>
                  {capitalize(exam.name)}
                </Text>
              </Checkbox>
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    );
  }, [favoriteExams.length, orderBy, orderedExams, selectedExams, onSelectExam]);

  return <Box w={'100%'}>{Content}</Box>;
}
